import { getFileExt } from '../types'

/**
 * GCC Client Taxonomy upload rules (WI 2332). Shared by the Step 1 picker and
 * the POST /api/ai/gcc schema so a file the UI turns away is also refused by
 * the server, instead of slipping through and failing as a generic 500.
 */
export const GCC_TAXONOMY_EXTS = ['.pdf', '.docx', '.pptx', '.xlsx'] as const

/** Matches the "max 20 MB" hint shown under the picker. */
export const GCC_TAXONOMY_MAX_BYTES = 20 * 1024 * 1024

const ACCEPTED_LABEL = GCC_TAXONOMY_EXTS.map((e) => e.slice(1).toUpperCase()).join(', ')

/** True when the extension (case-insensitive) is one the GCC pipeline can parse. */
export function isGccTaxonomyFile(name: string): boolean {
  return (GCC_TAXONOMY_EXTS as readonly string[]).includes(getFileExt(name))
}

export function gccUnsupportedTypeMessage(name: string): string {
  return `"${name}" is not a supported file type. Accepted: ${ACCEPTED_LABEL}.`
}

/**
 * Decoded byte length of a base64 string, without decoding it. Lets the server
 * apply the 20 MB limit to the base64 uploads it receives (WI 2364).
 */
export function base64DecodedBytes(b64: string): number {
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0
  return Math.max(0, Math.floor((b64.length * 3) / 4) - padding)
}

/**
 * Extracted text at or below this many characters (after trimming) counts as an
 * empty document (WI 2363). Same threshold Inside-Out uses in extractCombinedText.
 */
export const GCC_TAXONOMY_MIN_TEXT_CHARS = 20

export function gccEmptyFileMessage(name: string): string {
  return `"${name}" is empty.`
}

export function gccNoReadableTextMessage(name: string): string {
  return `"${name}" contains no readable text. Upload a client taxonomy document with content.`
}

/** True when extracted text is too short to ground the task register. */
export function isGccTaxonomyTextEmpty(text: string | null | undefined): boolean {
  return (text ?? '').trim().length <= GCC_TAXONOMY_MIN_TEXT_CHARS
}

/**
 * Why a taxonomy upload would be rejected, or null when it is acceptable.
 * Size is optional so a name-only check stays possible.
 */
export function gccTaxonomyRejection(name: string, sizeBytes?: number): string | null {
  if (!isGccTaxonomyFile(name)) return gccUnsupportedTypeMessage(name)
  if (sizeBytes === 0) return gccEmptyFileMessage(name)
  if (sizeBytes !== undefined && sizeBytes > GCC_TAXONOMY_MAX_BYTES) {
    return `"${name}" is larger than the 20 MB limit.`
  }
  return null
}
